import React, { useEffect, useState } from 'react';
import {
  Drawer, Box, Typography, Stack, Chip, Divider, Tab, Tabs, IconButton,
  CircularProgress, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Alert, Avatar, Rating, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Autocomplete, Card, CardContent
} from '@mui/material';
import {
  Close, Hotel, DirectionsCar, Assignment, Warning, CheckCircle,
  NoteAdd, Star, EditOutlined as EditOutlinedIcon, AddComment as AddCommentIcon,
  OpenInNew as OpenInNewIcon, VisibilityOutlined as VisibilityOutlinedIcon,
  Download as DownloadIcon, Refresh as RefreshIcon, Add as AddIcon,
  DeleteOutline as DeleteOutlineIcon, Person as PersonIcon, Phone as PhoneIcon,
  Email as EmailIcon, CalendarMonth as CalendarMonthIcon, Groups as GroupsIcon,
  BedroomParent as BedroomParentIcon, LocationOn as LocationOnIcon,
  ErrorOutline as ErrorOutlineIcon, Verified as VerifiedIcon,
  PendingActions as PendingActionsIcon, Info as InfoIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  doUpdateStatus, doUpdateConfirmations, doAddNote, doUpdateFeedback,
  loadTripActivities, updateTripInList, loadAllTrips,
} from '../../../reduxcomponents/slices/tripManagementSlice';
import TripChecklist from './TripChecklist';
import ActivityTimeline from './ActivityTimeline';
import { RaiseEscalationDialog } from './EscalationDialog';
import CollectionFollowupDialog from './CollectionFollowup';
import dayjs from 'dayjs';
import usePermissions from '../../../hooks/UsePermissions';
import useSnackbar from '../../../hooks/useSnackbar';
import {
  updateFollowupDetails,
  addChangeRequest,
  addChangeRequestForItineray,
  getSingleOperation,
  GuestandTripUpdateByOpt
} from '../../../api/operationAPI';
import { getAllHotels } from '../../../api/hotelsAPI';
import { getAllVehicle } from '../../../api/vehicleAPI';
import {
  calculateSingleHotelCost,
  exportQueryViewPDF,
  mapOperationToPdfData
} from '../../createItinerary/createInquiryCalculation';

const STATUS_COLORS = {
  Upcoming: 'info', Running: 'success', Completed: 'default', Cancelled: 'error', 'On Hold': 'warning',
};

const MEAL_PLANS = [
  { value: 'cp_plan', label: 'CP (Breakfast)' },
  { value: 'map_plan', label: 'MAP (Breakfast + 1 Meal)' },
  { value: 'ap_plan', label: 'AP (All Meals)' },
  { value: 'ep_plan', label: 'EP (No Meals)' }
];

const getMealPlanLabel = (plan) => {
  const match = MEAL_PLANS.find(p => p.value === plan);
  return match ? match.label : (plan || '—');
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = dayjs(dateString);
  return d.isValid() ? d.format('DD MMM YYYY') : dateString;
};

/**
 * Advanced TripDetailDrawer
 * Provides full end-to-end trip management directly in the drawer with parity to /query/view/:id
 */
const TripDetailDrawer = ({ open, trip, onClose, isManager = false }) => {
  const dispatch = useDispatch();
  const { loading, filters } = useSelector(s => s.tripManagement);
  const userRole = useSelector(state => state.tokens?.user?.permission);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [tab, setTab] = useState(0);
  const [note, setNote] = useState('');
  const [statusUpdate, setStatusUpdate] = useState('');
  const [feedback, setFeedback] = useState({ rating: 0, comment: '' });
  const [localTrip, setLocalTrip] = useState(null);
  const [escalateDialog, setEscalateDialog] = useState(false);
  const [collectDialog, setCollectDialog] = useState(false);
  const [actionError, setActionError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const hasPermission = usePermissions();
  const canEditItinerary = hasPermission("operation", "alter");
  const canCreateChangeRequest = hasPermission("operation", "change-request");
  const canVerifyItinerary = hasPermission("operation", "verify");
  const canEditCards = userRole === 'Admin' || userRole === 'Operation Manager';

  // Master Data
  const [hotels, setHotels] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  // Change Request Dialog state
  const [changeRequestOpen, setChangeRequestOpen] = useState(false);
  const [changeRequestDesc, setChangeRequestDesc] = useState('');
  const [crSubmitting, setCrSubmitting] = useState(false);

  // Edit Day Itinerary Dialog state
  const [editDayOpen, setEditDayOpen] = useState(false);
  const [editDayIndex, setEditDayIndex] = useState(null);
  const [editDayData, setEditDayData] = useState({});
  const [editDaySubmitting, setEditDaySubmitting] = useState(false);

  // Verify Day Dialog state
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyIndex, setVerifyIndex] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState('');
  const [verifyReason, setVerifyReason] = useState('');
  const [verifySubmitting, setVerifySubmitting] = useState(false);

  // View Rejections Dialog state
  const [rejectedViewOpen, setRejectedViewOpen] = useState(false);

  // Edit Guest Dialog state
  const [guestEditOpen, setGuestEditOpen] = useState(false);
  const [guestEditData, setGuestEditData] = useState({});
  const [guestEditSubmitting, setGuestEditSubmitting] = useState(false);

  // Edit Booking Dialog state
  const [bookingEditOpen, setBookingEditOpen] = useState(false);
  const [bookingEditData, setBookingEditData] = useState({});
  const [bookingEditSubmitting, setBookingEditSubmitting] = useState(false);

  // Derived options for currently selected hotel/vehicle in edit dialog
  const selectedHotelObj = hotels.find(h => h.hotel_name === editDayData.hotel_name || h._id === editDayData.hotel_id);
  const roomOptions = selectedHotelObj ? (selectedHotelObj.category || []).map(c => c.room_cat) : [];

  const matchedVehicleOwner = vehicles.find(v =>
    (v.vehicles || []).some(veh => veh.vehicle_name === editDayData.vehicle_name)
  );
  const driverOptions = (matchedVehicleOwner?.drivers && matchedVehicleOwner.drivers.length > 0)
    ? matchedVehicleOwner.drivers
    : vehicles.flatMap(v => v.drivers || []);

  const uniqueVehicleNames = Array.from(new Set(
    vehicles.flatMap(v => (v.vehicles || []).map(veh => veh.vehicle_name)).filter(Boolean)
  ));

  const hasHotelPrice = (hotelId) => {
    const hotel = hotels.find(h => h._id === hotelId);
    if (!hotel) return false;
    return hotel.category?.some(cat =>
      ['cp_plan', 'map_plan', 'ap_plan', 'ep_plan'].some(
        plan => (cat.season_price?.[plan] > 0) || (cat.off_season_price?.[plan] > 0)
      )
    );
  };

  const getPrice = (hotel, room_type, meal_plan, season) => {
    if (!hotel || !room_type || !meal_plan) return '';
    const cat = hotel.category?.find(c => c.room_cat === room_type);
    if (!cat) return '';
    const priceObj = season === 'Off Season' ? cat.off_season_price : cat.season_price;
    return priceObj ? priceObj[meal_plan] : '';
  };

  const refreshList = () => dispatch(loadAllTrips(filters));

  // Sync operation data from server
  const fetchOperationData = async (targetTrip) => {
    if (!targetTrip) return;
    const opId = targetTrip.operation_id || targetTrip._id;
    try {
      setSyncLoading(true);
      const op = await getSingleOperation(opId);
      if (op) {
        setLocalTrip(prev => {
          if (!prev) return prev;
          const updatedFollowup = (op.followup_details && op.followup_details.length > 0)
            ? op.followup_details
            : prev.followup_details;
          return {
            ...prev,
            followup_details: updatedFollowup,
            hotel_confirmed: updatedFollowup.some(f => f.hotel_confirmation && f.hotel_confirmation.trim() !== ''),
            driver_confirmed: updatedFollowup.some(f => f.driver && f.driver.trim() !== ''),
            permit_done: updatedFollowup.some(f => f.permit && f.permit.trim() !== ''),
          };
        });
      }
    } catch (e) {
      console.warn("Could not fetch operation by id directly:", e);
    } finally {
      setSyncLoading(false);
    }
  };

  // Sync the local copy whenever the trip prop changes
  useEffect(() => {
    if (open && trip) {
      setLocalTrip(trip);
      setTab(0);
      setStatusUpdate('');
      setNote('');
      setActionError('');
      fetchOperationData(trip);

      if (hotels.length === 0) {
        getAllHotels().then(setHotels).catch(console.error);
      }
      if (vehicles.length === 0) {
        getAllVehicle().then(setVehicles).catch(console.error);
      }
    }
    if (!open) {
      setLocalTrip(null);
    }
  }, [open, trip]);

  // Load operation activities when the Timeline tab is opened
  useEffect(() => {
    if (tab === 3 && localTrip?._id) {
      dispatch(loadTripActivities(localTrip._id));
    }
  }, [tab, localTrip, dispatch]);

  const handleOpenFullPage = () => {
    if (!localTrip) return;
    const targetId = localTrip.operation_id || localTrip._id;
    window.open(`/query/view/${targetId}`, '_blank');
  };

  const handleExportPDF = async (isPreview = false) => {
    if (!localTrip) return;
    try {
      setPdfLoading(true);
      const opId = localTrip.operation_id || localTrip._id;
      let operationData = null;
      try {
        operationData = await getSingleOperation(opId);
      } catch (e) {
        console.warn("Could not fetch operation for PDF:", e);
      }
      const queriesData = localTrip._raw || {};
      const { guestInfo, itineraryData } = mapOperationToPdfData(
        operationData,
        queriesData,
        localTrip._raw?.guest_info
      );
      await exportQueryViewPDF({ guestInfo, itineraryData }, { isPreview }, setPdfLoading, showSnackbar);
    } catch (err) {
      console.error("PDF export failed:", err);
      showSnackbar("Failed to generate PDF", "error");
    } finally {
      setPdfLoading(false);
    }
  };

  // Itinerary Day Edit handlers
  const handleOpenEdit = (day, index) => {
    const rawItinerary = localTrip._raw?.itinerary || [];
    const destinationFallback = day.destination || rawItinerary[index] || localTrip.destination || '';
    setEditDayData({
      journey_date: day.journey_date ? day.journey_date.slice(0, 10) : '',
      destination: destinationFallback,
      hotel_name: day.hotel_name || '',
      hotel_id: day.hotel_id || '',
      room_type: day.room_type || '',
      meal_plan: day.meal_plan || '',
      hotel_amount: day.hotel_amount || '',
      hotel_confirmation: day.hotel_confirmation || '',
      checkin_date: day.checkin_date ? day.checkin_date.slice(0, 10) : (day.journey_date ? day.journey_date.slice(0, 10) : ''),
      checkout_date: day.checkout_date ? day.checkout_date.slice(0, 10) : '',
      vehicle_name: day.vehicle_name || '',
      driver: day.driver || '',
      driver_phone: day.driver_phone || '',
      vehicle_payment: day.vehicle_payment || '',
      vehicle_status: day.vehicle_status || '',
      approved_status: day.approved_status || 'Pending',
      rejected_reason: day.rejected_reason || [],
    });
    setEditDayIndex(index);
    setEditDayOpen(true);
  };

  const handleEditDaySave = async () => {
    try {
      setEditDaySubmitting(true);
      const originalDay = (localTrip.followup_details || [])[editDayIndex] || {};
      const rawItinerary = localTrip._raw?.itinerary || [];
      const originalDestination = originalDay.destination || rawItinerary[editDayIndex] || localTrip.destination || '';

      const changedFields = [];
      if (editDayData.destination && editDayData.destination !== originalDestination) {
        changedFields.push(`destination: ${originalDestination || 'None'} -> ${editDayData.destination}`);
      }
      if (editDayData.hotel_name !== (originalDay.hotel_name || '')) {
        changedFields.push(`hotel: ${originalDay.hotel_name || 'None'} -> ${editDayData.hotel_name || 'None'}`);
      }
      if (editDayData.room_type !== (originalDay.room_type || '')) {
        changedFields.push(`room_type: ${originalDay.room_type || 'None'} -> ${editDayData.room_type || 'None'}`);
      }
      if (editDayData.meal_plan !== (originalDay.meal_plan || '')) {
        changedFields.push(`meal_plan: ${originalDay.meal_plan || 'None'} -> ${editDayData.meal_plan || 'None'}`);
      }
      if (editDayData.hotel_amount !== (originalDay.hotel_amount || '')) {
        changedFields.push(`hotel_amount: ₹${originalDay.hotel_amount || 0} -> ₹${editDayData.hotel_amount || 0}`);
      }
      if (editDayData.hotel_confirmation !== (originalDay.hotel_confirmation || '')) {
        changedFields.push(`hotel_confirmation: ${originalDay.hotel_confirmation || 'Pending'} -> ${editDayData.hotel_confirmation || 'Pending'}`);
      }
      if (editDayData.vehicle_name !== (originalDay.vehicle_name || '')) {
        changedFields.push(`vehicle: ${originalDay.vehicle_name || 'None'} -> ${editDayData.vehicle_name || 'None'}`);
      }
      if (editDayData.driver !== (originalDay.driver || '')) {
        changedFields.push(`driver: ${originalDay.driver || 'None'} -> ${editDayData.driver || 'None'}`);
      }
      if (editDayData.driver_phone !== (originalDay.driver_phone || '')) {
        changedFields.push(`driver_phone: ${originalDay.driver_phone || 'None'} -> ${editDayData.driver_phone || 'None'}`);
      }
      if (editDayData.vehicle_payment !== (originalDay.vehicle_payment || '')) {
        changedFields.push(`vehicle_payment: ₹${originalDay.vehicle_payment || 0} -> ₹${editDayData.vehicle_payment || 0}`);
      }
      if (editDayData.vehicle_status !== (originalDay.vehicle_status || '')) {
        changedFields.push(`vehicle_status: ${originalDay.vehicle_status || 'Pending'} -> ${editDayData.vehicle_status || 'Pending'}`);
      }
      if (editDayData.journey_date !== (originalDay.journey_date ? originalDay.journey_date.slice(0, 10) : '')) {
        changedFields.push(`journey_date: ${originalDay.journey_date?.slice(0, 10) || 'None'} -> ${editDayData.journey_date || 'None'}`);
      }

      const opId = localTrip.operation_id || localTrip._id;

      if (canEditItinerary) {
        // Direct save for managers
        const newFollowups = [...(localTrip.followup_details || [])];
        newFollowups[editDayIndex] = {
          ...newFollowups[editDayIndex],
          ...editDayData,
          approved_status: canVerifyItinerary ? (editDayData.approved_status || 'Pending') : 'Pending',
        };
        await updateFollowupDetails(opId, { followup_details: newFollowups });
        setLocalTrip(prev => ({ ...prev, followup_details: newFollowups }));
        setEditDayOpen(false);
        showSnackbar(`Day ${editDayIndex + 1} updated successfully`, "success");
        refreshList();
      } else if (canCreateChangeRequest) {
        // Change request for Operation users (same as SingleQueriesView)
        if (changedFields.length === 0) {
          showSnackbar("No changes detected.", "info");
          setEditDayOpen(false);
          return;
        }
        const changeDescription = `Day ${editDayIndex + 1} changes: ${changedFields.join(", ")}`;
        await addChangeRequestForItineray(opId, { description: changeDescription });
        showSnackbar("Change request submitted successfully for manager approval", "success");
        setEditDayOpen(false);
        refreshList();
      } else {
        showSnackbar("You do not have permission to modify or request changes.", "error");
      }
    } catch (err) {
      setActionError(err.response?.data?.message || err.message);
      showSnackbar(err.response?.data?.message || "Failed to process request", "error");
    } finally {
      setEditDaySubmitting(false);
    }
  };

  const handleAddExtraDay = async () => {
    try {
      const existing = localTrip.followup_details || [];
      let nextDate = new Date();
      if (existing.length > 0) {
        const lastDay = existing[existing.length - 1];
        if (lastDay.journey_date) {
          nextDate = new Date(lastDay.journey_date);
          nextDate.setDate(nextDate.getDate() + 1);
        }
      } else if (localTrip.travel_date) {
        nextDate = new Date(localTrip.travel_date);
      }
      const nextDateStr = nextDate.toISOString().split('T')[0];
      const nextCheckoutStr = new Date(nextDate.getTime() + 86400000).toISOString().split('T')[0];

      const newDay = {
        journey_date: nextDateStr,
        destination: localTrip.destination || '',
        hotel_name: '',
        hotel_id: '',
        hotel_amount: '',
        hotel_confirmation: '',
        checkin_date: nextDateStr,
        checkout_date: nextCheckoutStr,
        meal_plan: '',
        room_type: '',
        vehicle_name: '',
        vehicle_payment: '',
        vehicle_status: '',
        driver: '',
        driver_phone: '',
        approved_status: 'Pending',
        rejected_reason: []
      };

      const expandedItinerary = [...existing, newDay];
      const opId = localTrip.operation_id || localTrip._id;
      await updateFollowupDetails(opId, { followup_details: expandedItinerary });
      setLocalTrip(prev => ({ ...prev, followup_details: expandedItinerary }));
      showSnackbar("Extra day added to itinerary", "success");
      refreshList();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to add extra day", "error");
    }
  };

  const handleDeleteDay = async (indexToDelete) => {
    if (!window.confirm(`Are you sure you want to delete Day ${indexToDelete + 1}?`)) return;
    try {
      const updated = (localTrip.followup_details || []).filter((_, idx) => idx !== indexToDelete);
      const opId = localTrip.operation_id || localTrip._id;
      await updateFollowupDetails(opId, { followup_details: updated });
      setLocalTrip(prev => ({ ...prev, followup_details: updated }));
      showSnackbar(`Day ${indexToDelete + 1} deleted successfully`, "success");
      refreshList();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to delete day", "error");
    }
  };

  const handleOpenVerify = (index) => {
    setVerifyIndex(index);
    setVerifyStatus('');
    setVerifyReason('');
    setVerifyOpen(true);
  };

  const handleVerifySubmit = async () => {
    if (!verifyStatus || (verifyStatus === 'Rejected' && !verifyReason.trim())) return;
    try {
      setVerifySubmitting(true);
      const newFollowups = [...(localTrip.followup_details || [])];
      newFollowups[verifyIndex] = {
        ...newFollowups[verifyIndex],
        approved_status: verifyStatus,
        rejected_reason: verifyStatus === 'Rejected'
          ? [
              ...(newFollowups[verifyIndex].rejected_reason || []),
              { description: verifyReason.trim(), date_time: new Date().toISOString() }
            ]
          : (newFollowups[verifyIndex].rejected_reason || [])
      };
      
      const opId = localTrip.operation_id || localTrip._id;
      await updateFollowupDetails(opId, { followup_details: newFollowups });
      setLocalTrip(prev => ({ ...prev, followup_details: newFollowups }));
      setVerifyOpen(false);
      showSnackbar(`Day ${verifyIndex + 1} marked as ${verifyStatus}`, "success");
      refreshList();
    } catch (err) {
      setActionError(err.response?.data?.message || err.message);
      showSnackbar(err.response?.data?.message || "Verification failed", "error");
    } finally {
      setVerifySubmitting(false);
    }
  };

  const handleCRSubmit = async () => {
    if (!changeRequestDesc.trim()) return;
    try {
      setCrSubmitting(true);
      const opId = localTrip.operation_id || localTrip._id;
      await addChangeRequest(opId, { description: changeRequestDesc });
      setChangeRequestOpen(false);
      setChangeRequestDesc('');
      showSnackbar("Change request submitted successfully", "success");
    } catch (err) {
      setActionError(err.response?.data?.message || err.message);
      showSnackbar(err.response?.data?.message || "Failed to submit change request", "error");
    } finally {
      setCrSubmitting(false);
    }
  };

  // Guest Info Edit Handlers
  const handleOpenGuestEdit = () => {
    if (!canEditCards) {
      setChangeRequestDesc(`Change Request for Guest & Stay Details: `);
      setChangeRequestOpen(true);
      return;
    }
    const raw = localTrip._raw || {};
    setGuestEditData({
      name: localTrip.guest_id?.name || raw.guest_info?.guest_name || '',
      contact: localTrip.guest_id?.phone || raw.guest_info?.guest_phone || '',
      country_code: localTrip.guest_id?.country_code || raw.guest_info?.guest_country_code || '+91',
      email: localTrip.guest_id?.email || raw.guest_info?.guest_email || '',
      pax: localTrip.total_pax || raw.pax || 2,
      kids_above_5: raw.kids_above_5 || 0,
      hotel: raw.stay_info?.hotel || '',
      roomType: raw.stay_info?.room_type || '',
      rooms: raw.stay_info?.rooms || 1,
      carname: raw.car_details?.[0]?.car_name || raw.car_details?.car_name || '',
      carcount: raw.car_details?.[0]?.car_count || raw.car_details?.car_count || 1,
    });
    setGuestEditOpen(true);
  };

  const handleSaveGuestEdit = async () => {
    if (!canEditCards) {
      showSnackbar("Operation users cannot edit guest details directly. Please send a change request.", "warning");
      return;
    }
    try {
      setGuestEditSubmitting(true);
      const opId = localTrip.operation_id || localTrip._id;
      const guestId = localTrip._raw?.guest_id?._id || localTrip._raw?.guest_id || '000000000000000000000000';
      await GuestandTripUpdateByOpt(guestId, opId, guestEditData);
      setLocalTrip(prev => ({
        ...prev,
        guest_id: {
          ...prev.guest_id,
          name: guestEditData.name,
          phone: guestEditData.contact,
          country_code: guestEditData.country_code,
          email: guestEditData.email,
        },
        total_pax: Number(guestEditData.pax) || prev.total_pax,
        _raw: {
          ...prev._raw,
          guest_info: {
            ...prev._raw?.guest_info,
            guest_name: guestEditData.name,
            guest_phone: guestEditData.contact,
            guest_country_code: guestEditData.country_code,
            guest_email: guestEditData.email,
          },
          pax: guestEditData.pax,
          kids_above_5: guestEditData.kids_above_5,
          stay_info: {
            ...prev._raw?.stay_info,
            hotel: guestEditData.hotel,
            room_type: guestEditData.roomType,
            rooms: guestEditData.rooms,
          }
        }
      }));
      setGuestEditOpen(false);
      showSnackbar("Guest & Stay Information updated successfully", "success");
      refreshList();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to update guest details", "error");
    } finally {
      setGuestEditSubmitting(false);
    }
  };

  // Booking Info Edit Handlers
  const handleOpenBookingEdit = () => {
    if (!canEditCards) {
      setChangeRequestDesc(`Change Request for Booking & Financial Details: `);
      setChangeRequestOpen(true);
      return;
    }
    const raw = localTrip._raw || {};
    setBookingEditData({
      source: raw.lead_source || '',
      tourDate: localTrip.travel_date ? dayjs(localTrip.travel_date).format('YYYY-MM-DD') : (raw.travel_date ? raw.travel_date.slice(0, 10) : ''),
      bookingStatus: raw.lead_stage || localTrip.trip_status || 'Upcoming',
      cost: localTrip.payment?.total_amount || raw.cost || '',
      advancePayment: localTrip.payment?.paid_amount || raw.advance || '',
    });
    setBookingEditOpen(true);
  };

  const handleSaveBookingEdit = async () => {
    if (!canEditCards) {
      showSnackbar("Operation users cannot edit booking and financial details directly. Please send a change request.", "warning");
      return;
    }
    try {
      setBookingEditSubmitting(true);
      const opId = localTrip.operation_id || localTrip._id;
      const guestId = localTrip._raw?.guest_id?._id || localTrip._raw?.guest_id || '000000000000000000000000';
      await GuestandTripUpdateByOpt(guestId, opId, bookingEditData);
      const newTotal = Number(bookingEditData.cost) || localTrip.payment?.total_amount || 0;
      const newPaid = Number(bookingEditData.advancePayment) || localTrip.payment?.paid_amount || 0;
      setLocalTrip(prev => ({
        ...prev,
        travel_date: bookingEditData.tourDate ? new Date(bookingEditData.tourDate) : prev.travel_date,
        trip_status: bookingEditData.bookingStatus || prev.trip_status,
        payment: {
          ...prev.payment,
          total_amount: newTotal,
          paid_amount: newPaid,
          due_amount: Math.max(0, newTotal - newPaid),
        },
        _raw: {
          ...prev._raw,
          lead_source: bookingEditData.source,
          lead_stage: bookingEditData.bookingStatus,
          travel_date: bookingEditData.tourDate,
          cost: newTotal,
          advance: newPaid,
        }
      }));
      setBookingEditOpen(false);
      showSnackbar("Booking details updated successfully", "success");
      refreshList();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to update booking details", "error");
    } finally {
      setBookingEditSubmitting(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!statusUpdate || !localTrip) return;
    setActionError('');
    try {
      await dispatch(doUpdateStatus({ id: localTrip._id, status: statusUpdate })).unwrap();
      setLocalTrip(t => ({ ...t, trip_status: statusUpdate }));
      dispatch(updateTripInList({ id: localTrip._id, changes: { trip_status: statusUpdate } }));
      showSnackbar(`Status updated to ${statusUpdate}`, "success");
    } catch (err) {
      setActionError(err || 'Failed to update status');
    }
  };

  const handleConfirmation = async (field, value) => {
    if (!localTrip) return;
    setActionError('');
    try {
      await dispatch(doUpdateConfirmations({
        id:          localTrip._id,
        operationId: localTrip.operation_id || localTrip._id,
        data:        { [field]: value },
      })).unwrap();
      setLocalTrip(t => ({ ...t, [field]: value }));
      dispatch(updateTripInList({ id: localTrip._id, changes: { [field]: value } }));
      showSnackbar("Confirmation updated", "success");
    } catch (err) {
      setActionError(err || 'Failed to update confirmation');
    }
  };

  const handleAddNote = async () => {
    if (!note.trim() || !localTrip) return;
    setActionError('');
    try {
      await dispatch(doAddNote({
        id:          localTrip._id,
        operationId: localTrip.operation_id || localTrip._id,
        data:        { content: note },
      })).unwrap();
      setNote('');
      showSnackbar("Note added successfully", "success");
    } catch (err) {
      setActionError(err || 'Failed to add note');
    }
  };

  const handleFeedback = async () => {
    if (!localTrip) return;
    setActionError('');
    try {
      await dispatch(doUpdateFeedback({ id: localTrip._id, data: feedback })).unwrap();
      setLocalTrip(t => ({ ...t, guest_feedback: feedback }));
      showSnackbar("Feedback saved successfully", "success");
    } catch (err) {
      setActionError(err || 'Failed to save feedback');
    }
  };

  // Rejections list for View Rejections popup
  const rejectedDays = (localTrip?.followup_details || []).map((day, idx) => ({
    dayNum: idx + 1,
    destination: day.destination || localTrip?._raw?.itinerary?.[idx] || localTrip?.destination || 'Destination TBD',
    reasons: day.rejected_reason || []
  })).filter(d => d.reasons.length > 0);

  const verifiedCount = (localTrip?.followup_details || []).filter(d => d.approved_status === 'Approved').length;
  const totalDaysCount = (localTrip?.followup_details || []).length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 660, md: 740, lg: 780 },
          maxWidth: '100vw',
          boxShadow: '-8px 0 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <Box height="100%" display="flex" flexDirection="column" bgcolor="#fafbfc">

        {/* ── HEADER ──────────────────────────────────────────────────────── */}
        <Box px={3} py={2} bgcolor="background.paper" borderBottom={1} borderColor="divider">
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="h6" fontWeight={750} color="text.primary">
                  Trip Details
                </Typography>
                {localTrip && (
                  <Chip
                    label={localTrip.trip_status}
                    color={STATUS_COLORS[localTrip.trip_status] || 'default'}
                    size="small"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                )}
              </Stack>
              {localTrip && (
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  Guest: <strong style={{ color: '#1e293b' }}>{localTrip.guest_id?.name || '—'}</strong> 
                  {localTrip.booking_ref && ` • Ref: #${localTrip.booking_ref.slice(-6)}`}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1} alignItems="center">
              {localTrip && (
                <>
                  <Tooltip title="Open in Full Query View (/query/view/:id)">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<OpenInNewIcon fontSize="small" />}
                      onClick={handleOpenFullPage}
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                      Full Page
                    </Button>
                  </Tooltip>
                  <Tooltip title="Preview PDF">
                    <IconButton
                      size="small"
                      onClick={() => handleExportPDF(true)}
                      disabled={pdfLoading}
                      sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}
                    >
                      {pdfLoading ? <CircularProgress size={16} /> : <VisibilityOutlinedIcon fontSize="small" color="primary" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download PDF">
                    <IconButton
                      size="small"
                      onClick={() => handleExportPDF(false)}
                      disabled={pdfLoading}
                      sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}
                    >
                      <DownloadIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Refresh Trip Data">
                    <IconButton
                      size="small"
                      onClick={() => fetchOperationData(localTrip)}
                      disabled={syncLoading}
                      sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}
                    >
                      {syncLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <IconButton onClick={onClose} size="small" sx={{ ml: 0.5 }}>
                <Close />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        {!localTrip && (
          <Box flex={1} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        )}

        {localTrip && (
          <>
            {/* ── QUICK SUMMARY BAR ────────────────────────────────────────── */}
            <Box px={3} py={1.5} bgcolor="#f1f5f9" borderBottom={1} borderColor="divider">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    DESTINATION
                  </Typography>
                  <Typography variant="body2" fontWeight={700} noWrap title={localTrip.destination}>
                    {localTrip.destination || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    TRAVEL DATE
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatDate(localTrip.travel_date)}
                  </Typography>
                </Grid>
                <Grid item xs={4} sm={2}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    RETURN
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {formatDate(localTrip.return_date)}
                  </Typography>
                </Grid>
                <Grid item xs={4} sm={1.5}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    PAX
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {localTrip.total_pax || 0}
                  </Typography>
                </Grid>
                <Grid item xs={4} sm={3}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    EXECUTIVES
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    {localTrip.allocation && localTrip.allocation.length > 0 ? (
                      localTrip.allocation.map((alloc, idx) => (
                        <Chip
                          key={idx}
                          avatar={<Avatar sx={{ width: 16, height: 16, fontSize: 10 }}>{alloc.user_id?.fullName?.[0] || '?'}</Avatar>}
                          label={alloc.user_id?.fullName || 'Assigned'}
                          size="small"
                          sx={{ height: 22, fontSize: 11, fontWeight: 600, my: 0.2 }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.disabled">Unassigned</Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* ── TABS NAVIGATION ─────────────────────────────────────────── */}
            <Box bgcolor="background.paper" borderBottom={1} borderColor="divider" px={2}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: 12.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em'
                  }
                }}
              >
                <Tab label="Overview" />
                <Tab
                  label={
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <span>Itinerary</span>
                      {totalDaysCount > 0 && (
                        <Chip
                          label={`${verifiedCount}/${totalDaysCount}`}
                          size="small"
                          color={verifiedCount === totalDaysCount ? "success" : "default"}
                          sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                        />
                      )}
                    </Stack>
                  }
                />
                <Tab label="Checklist" />
                <Tab label="Timeline" />
                <Tab label="Escalations" />
              </Tabs>
            </Box>

            {/* ── TAB CONTENT BODY ────────────────────────────────────────── */}
            <Box flex={1} overflow="auto" p={2.5}>
              {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}

              {/* ── 0. OVERVIEW TAB ───────────────────────────────────────── */}
              {tab === 0 && (
                <Stack spacing={2.5}>
                  
                  {/* Guest & Stay Information Card */}
                  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#ffffff' }}>
                    <Box p={2} bgcolor="#f8fafc" borderBottom="1px solid #e2e8f0" display="flex" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ bgcolor: '#3b82f6', width: 28, height: 28 }}>
                          <PersonIcon sx={{ fontSize: 17 }} />
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight={750} color="text.primary">
                          Guest & Stay Information
                        </Typography>
                      </Stack>
                      {canEditCards ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditOutlinedIcon fontSize="small" />}
                          onClick={handleOpenGuestEdit}
                          sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, borderRadius: 1.5, py: 0.3 }}
                        >
                          Edit Guest
                        </Button>
                      ) : canCreateChangeRequest ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<AddCommentIcon fontSize="small" />}
                          onClick={() => {
                            setChangeRequestDesc(`Change Request for Guest & Stay Details: `);
                            setChangeRequestOpen(true);
                          }}
                          sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, borderRadius: 1.5, py: 0.3 }}
                        >
                          Request Change
                        </Button>
                      ) : null}
                    </Box>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <PersonIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>GUEST NAME</Typography>
                              <Typography variant="body2" fontWeight={600}>{localTrip.guest_id?.name || '—'}</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <PhoneIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>CONTACT</Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {localTrip.guest_id?.country_code || '+91'} {localTrip.guest_id?.phone || '—'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <EmailIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>EMAIL</Typography>
                              <Typography variant="body2" fontWeight={600} noWrap>{localTrip.guest_id?.email || '—'}</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <GroupsIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>PAX & KIDS</Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {localTrip.total_pax || 0} Adults {localTrip._raw?.kids_above_5 ? `• ${localTrip._raw.kids_above_5} Kids` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Hotel sx={{ color: '#94a3b8', fontSize: 18 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>HOTEL / PREFERRED</Typography>
                              <Typography variant="body2" fontWeight={600}>{localTrip._raw?.stay_info?.hotel || 'Standard Accommodations'}</Typography>
                            </Box>
                          </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <BedroomParentIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>ROOMS & CATEGORY</Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {localTrip._raw?.stay_info?.rooms || 1} Rooms {localTrip._raw?.stay_info?.room_type ? `(${localTrip._raw.stay_info.room_type})` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Booking & Financial Details Card */}
                  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#ffffff' }}>
                    <Box p={2} bgcolor="#f8fafc" borderBottom="1px solid #e2e8f0" display="flex" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar sx={{ bgcolor: '#10b981', width: 28, height: 28 }}>
                          <AccountBalanceWalletIcon sx={{ fontSize: 17 }} />
                        </Avatar>
                        <Typography variant="subtitle2" fontWeight={750} color="text.primary">
                          Booking & Financial Details
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => setCollectDialog(true)}
                          sx={{ textTransform: 'none', fontSize: 11, fontWeight: 700, borderRadius: 1.5, py: 0.3 }}
                        >
                          Collect Payment
                        </Button>
                        {canEditCards ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditOutlinedIcon fontSize="small" />}
                            onClick={handleOpenBookingEdit}
                            sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, borderRadius: 1.5, py: 0.3 }}
                          >
                            Edit Booking
                          </Button>
                        ) : canCreateChangeRequest ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<AddCommentIcon fontSize="small" />}
                            onClick={() => {
                              setChangeRequestDesc(`Change Request for Booking & Financial Details: `);
                              setChangeRequestOpen(true);
                            }}
                            sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, borderRadius: 1.5, py: 0.3 }}
                          >
                            Request Change
                          </Button>
                        ) : null}
                      </Stack>
                    </Box>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>SOURCE</Typography>
                          <Typography variant="body2" fontWeight={600}>{localTrip._raw?.lead_source || 'Direct'}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>BOOKING STATUS</Typography>
                          <Typography variant="body2" fontWeight={700} color="primary.main">
                            {localTrip._raw?.lead_stage || localTrip.trip_status || '—'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>TOUR DATE</Typography>
                          <Typography variant="body2" fontWeight={600}>{formatDate(localTrip.travel_date)}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>PAYMENT STATUS</Typography>
                          <Chip
                            label={localTrip.payment?.status || 'Pending'}
                            size="small"
                            color={localTrip.payment?.status === 'Paid' ? 'success' : localTrip.payment?.status === 'Overdue' ? 'error' : 'warning'}
                            sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box p={1.5} bgcolor="#f8fafc" borderRadius={2} border="1px dashed #cbd5e1">
                            <Stack direction="row" justifyContent="space-around" textAlign="center">
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>TOTAL PACKAGE COST</Typography>
                                <Typography variant="h6" fontWeight={800} color="text.primary">
                                  ₹{(localTrip.payment?.total_amount || 0).toLocaleString()}
                                </Typography>
                              </Box>
                              <Divider orientation="vertical" flexItem />
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>PAID AMOUNT</Typography>
                                <Typography variant="h6" fontWeight={800} color="success.main">
                                  ₹{(localTrip.payment?.paid_amount || 0).toLocaleString()}
                                </Typography>
                              </Box>
                              <Divider orientation="vertical" flexItem />
                              <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600}>BALANCE DUE</Typography>
                                <Typography variant="h6" fontWeight={800} color="error.main">
                                  ₹{(localTrip.payment?.due_amount || 0).toLocaleString()}
                                </Typography>
                              </Box>
                            </Stack>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* Status & Operational Confirmations */}
                  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#ffffff' }}>
                    <Box p={2} bgcolor="#f8fafc" borderBottom="1px solid #e2e8f0">
                      <Typography variant="subtitle2" fontWeight={750} color="text.primary">
                        Operations & Confirmations
                      </Typography>
                    </Box>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Trip Status Update */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center" mb={2}>
                        <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
                          <InputLabel>Update Trip Status</InputLabel>
                          <Select
                            value={statusUpdate}
                            label="Update Trip Status"
                            onChange={e => setStatusUpdate(e.target.value)}
                          >
                            {['Upcoming', 'Running', 'Completed', 'Cancelled', 'On Hold'].map(s => (
                              <MenuItem key={s} value={s}>{s}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleStatusUpdate}
                          disabled={!statusUpdate || loading.action}
                          sx={{ textTransform: 'none', fontWeight: 600, px: 3, height: 40 }}
                        >
                          Update Status
                        </Button>
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Confirmations Quick Toggles */}
                      <Typography variant="caption" color="text.secondary" fontWeight={700} display="block" mb={1}>
                        VENDOR & LOGISTICS CONFIRMATIONS
                      </Typography>
                      <Stack spacing={1}>
                        {[
                          { field: 'hotel_confirmed',  label: 'Hotel Confirmed',  icon: Hotel,         value: localTrip.hotel_confirmed },
                          { field: 'driver_confirmed', label: 'Driver Confirmed', icon: DirectionsCar, value: localTrip.driver_confirmed },
                          { field: 'permit_done',      label: 'Permit Done',      icon: Assignment,    value: localTrip.permit_done },
                        ].map(({ field, label, icon: Icon, value }) => (
                          <Stack key={field} direction="row" justifyContent="space-between" alignItems="center" p={1} bgcolor="#f8fafc" borderRadius={1.5}>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                              <Icon fontSize="small" color={value ? 'success' : 'action'} />
                              <Typography variant="body2" fontWeight={600}>{label}</Typography>
                              <Chip
                                label={value ? 'Confirmed' : 'Pending'}
                                size="small"
                                color={value ? 'success' : 'default'}
                                sx={{ height: 18, fontSize: 9, fontWeight: 700 }}
                              />
                            </Stack>
                            <Stack direction="row" spacing={0.5}>
                              <Button
                                size="small"
                                variant={value ? 'contained' : 'outlined'}
                                color="success"
                                onClick={() => handleConfirmation(field, true)}
                                disabled={loading.action}
                                sx={{ minWidth: 32, p: '2px 8px', fontWeight: 700 }}
                              >✓</Button>
                              <Button
                                size="small"
                                variant={!value ? 'contained' : 'outlined'}
                                color="error"
                                onClick={() => handleConfirmation(field, false)}
                                disabled={loading.action}
                                sx={{ minWidth: 32, p: '2px 8px', fontWeight: 700 }}
                              >✗</Button>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Notes & Activity */}
                  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#ffffff' }}>
                    <Box p={2} bgcolor="#f8fafc" borderBottom="1px solid #e2e8f0">
                      <Typography variant="subtitle2" fontWeight={750} color="text.primary">
                        Add Operation Note
                      </Typography>
                    </Box>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack spacing={1.5}>
                        <TextField
                          multiline
                          rows={2.5}
                          fullWidth
                          size="small"
                          placeholder="Type an internal note regarding this trip..."
                          value={note}
                          onChange={e => setNote(e.target.value)}
                        />
                        <Box textAlign="right">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleAddNote}
                            disabled={!note.trim() || loading.action}
                            startIcon={<NoteAdd />}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            Save Note
                          </Button>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>

                  {/* Guest Feedback */}
                  <Card elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2.5, bgcolor: '#ffffff' }}>
                    <Box p={2} bgcolor="#f8fafc" borderBottom="1px solid #e2e8f0">
                      <Typography variant="subtitle2" fontWeight={750} color="text.primary">
                        Guest Feedback
                      </Typography>
                    </Box>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {localTrip.guest_feedback?.rating ? (
                        <Box>
                          <Rating value={localTrip.guest_feedback.rating} readOnly size="small" />
                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            {localTrip.guest_feedback.comment || 'No review comment entered.'}
                          </Typography>
                        </Box>
                      ) : (
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" fontWeight={600}>Rating:</Typography>
                            <Rating
                              value={feedback.rating}
                              onChange={(_, v) => setFeedback(f => ({ ...f, rating: v }))}
                              size="medium"
                            />
                          </Stack>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Guest comments, satisfaction notes..."
                            value={feedback.comment}
                            onChange={e => setFeedback(f => ({ ...f, comment: e.target.value }))}
                          />
                          <Box textAlign="right">
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleFeedback}
                              disabled={!feedback.rating || loading.action}
                              startIcon={<Star />}
                              sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                              Save Feedback
                            </Button>
                          </Box>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>

                </Stack>
              )}

              {/* ── 1. ITINERARY TAB (ENHANCED) ────────────────────────────── */}
              {tab === 1 && (
                <Stack spacing={2.5}>
                  
                  {/* Top Bar for Itinerary */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1" fontWeight={800} color="text.primary">
                          Trip Itinerary
                        </Typography>
                        <Chip
                          icon={<VerifiedIcon sx={{ fontSize: '14px !important' }} />}
                          label={`${verifiedCount} of ${totalDaysCount} Days Verified`}
                          color={verifiedCount === totalDaysCount && totalDaysCount > 0 ? 'success' : 'default'}
                          size="small"
                          sx={{ fontWeight: 700, fontSize: 11 }}
                        />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Manage hotels, vehicle assignments, and verify day-wise plans
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      {rejectedDays.length > 0 && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<ErrorOutlineIcon />}
                          onClick={() => setRejectedViewOpen(true)}
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                        >
                          View Rejections ({rejectedDays.length})
                        </Button>
                      )}

                      {canCreateChangeRequest && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddCommentIcon />}
                          onClick={() => setChangeRequestOpen(true)}
                          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                        >
                          Change Request
                        </Button>
                      )}

                      {canEditItinerary && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleAddExtraDay}
                          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
                        >
                          Add Day
                        </Button>
                      )}
                    </Stack>
                  </Stack>

                  {(!localTrip.followup_details || localTrip.followup_details.length === 0) && (
                    <Alert severity="info" icon={<InfoIcon />}>
                      No itinerary details available for this trip yet. Click <strong>"Add Day"</strong> to start creating days.
                    </Alert>
                  )}

                  {/* Day Cards */}
                  {(localTrip.followup_details || []).map((day, idx) => {
                    const rawItinerary = localTrip._raw?.itinerary || [];
                    const dayDestination = day.destination || rawItinerary[idx] || localTrip.destination || 'Destination TBD';
                    const hasRejection = day.rejected_reason && day.rejected_reason.length > 0;
                    const latestRejection = hasRejection ? day.rejected_reason[day.rejected_reason.length - 1] : null;

                    return (
                      <Card
                        key={idx}
                        elevation={0}
                        sx={{
                          border: '1px solid',
                          borderColor: day.approved_status === 'Rejected' ? '#fecaca' : day.approved_status === 'Approved' ? '#bbf7d0' : '#e2e8f0',
                          borderRadius: 2.5,
                          bgcolor: '#ffffff',
                          transition: 'all 0.2s',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                            borderColor: '#cbd5e1'
                          }
                        }}
                      >
                        {/* Day Card Header */}
                        <Box
                          p={1.75}
                          bgcolor={day.approved_status === 'Approved' ? '#f0fdf4' : day.approved_status === 'Rejected' ? '#fef2f2' : '#f8fafc'}
                          borderBottom="1px solid"
                          borderColor={day.approved_status === 'Rejected' ? '#fee2e2' : '#e2e8f0'}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 28,
                                  height: 28,
                                  fontSize: 12,
                                  fontWeight: 800,
                                  bgcolor: day.approved_status === 'Approved' ? 'success.main' : day.approved_status === 'Rejected' ? 'error.main' : 'primary.main',
                                  color: '#fff'
                                }}
                              >
                                D{idx + 1}
                              </Avatar>
                              <Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <LocationOnIcon sx={{ color: '#ef4444', fontSize: 16 }} />
                                  <Typography variant="subtitle2" fontWeight={750} color="text.primary">
                                    Day {idx + 1} - {dayDestination}
                                  </Typography>
                                </Stack>
                                {day.journey_date && (
                                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ ml: 2.7 }}>
                                    {formatDate(day.journey_date)}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>

                            <Stack direction="row" spacing={1} alignItems="center">
                              {/* Approved Status Chip */}
                              {day.approved_status === 'Approved' && (
                                <Chip
                                  label="Approved"
                                  color="success"
                                  size="small"
                                  icon={<VerifiedIcon sx={{ fontSize: '13px !important' }} />}
                                  sx={{ fontWeight: 700, fontSize: 10, height: 22 }}
                                />
                              )}
                              {day.approved_status === 'Rejected' && (
                                <Chip
                                  label="Rejected"
                                  color="error"
                                  size="small"
                                  icon={<ErrorOutlineIcon sx={{ fontSize: '13px !important' }} />}
                                  sx={{ fontWeight: 700, fontSize: 10, height: 22 }}
                                />
                              )}
                              {(!day.approved_status || day.approved_status === 'Pending') && (
                                <Chip
                                  label="Pending"
                                  color="warning"
                                  size="small"
                                  icon={<PendingActionsIcon sx={{ fontSize: '13px !important' }} />}
                                  sx={{ fontWeight: 700, fontSize: 10, height: 22 }}
                                />
                              )}

                              {/* Action Buttons */}
                              {(canEditItinerary || canCreateChangeRequest) && (
                                <Tooltip title={canEditItinerary ? "Edit Day Details" : "Propose Day Changes (Change Request)"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEdit(day, idx)}
                                    sx={{ border: '1px solid #cbd5e1', bgcolor: '#fff', borderRadius: 1.5, p: 0.5 }}
                                  >
                                    <EditOutlinedIcon fontSize="small" color={canEditItinerary ? "primary" : "warning"} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {canVerifyItinerary && (
                                <Tooltip title="Approve / Reject Day">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenVerify(idx)}
                                    sx={{ border: '1px solid #cbd5e1', bgcolor: '#fff', borderRadius: 1.5, p: 0.5 }}
                                  >
                                    <CheckCircle fontSize="small" color={day.approved_status === 'Approved' ? 'success' : 'action'} />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {canEditItinerary && (
                                <Tooltip title="Delete Day">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteDay(idx)}
                                    sx={{ border: '1px solid #cbd5e1', bgcolor: '#fff', borderRadius: 1.5, p: 0.5 }}
                                  >
                                    <DeleteOutlineIcon fontSize="small" color="error" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </Stack>
                        </Box>

                        {/* Day Card Details Body */}
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Grid container spacing={2}>
                            
                            {/* Hotel Details Column */}
                            <Grid item xs={12} sm={6}>
                              <Box p={1.5} bgcolor="#f8fafc" borderRadius={2} border="1px solid #f1f5f9" height="100%">
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Avatar sx={{ bgcolor: '#e0f2fe', color: '#0284c7', width: 26, height: 26 }}>
                                      <Hotel sx={{ fontSize: 15 }} />
                                    </Avatar>
                                    <Typography variant="caption" color="text.secondary" fontWeight={750}>
                                      ACCOMMODATION
                                    </Typography>
                                  </Stack>
                                  {day.hotel_confirmation && (
                                    <Chip
                                      label={day.hotel_confirmation}
                                      size="small"
                                      color={day.hotel_confirmation === 'Confirmed' ? 'success' : 'warning'}
                                      sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="body2" fontWeight={700} color="text.primary" mt={0.5}>
                                  {day.hotel_name || 'No Hotel Selected'}
                                  {day.hotel_amount ? ` (₹${Number(day.hotel_amount).toLocaleString()})` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {day.room_type || 'Room TBD'} • {getMealPlanLabel(day.meal_plan)}
                                </Typography>
                                {(day.checkin_date || day.checkout_date) && (
                                  <Typography variant="caption" color="text.disabled" display="block" mt={0.5}>
                                    In: {formatDate(day.checkin_date)} | Out: {formatDate(day.checkout_date)}
                                  </Typography>
                                )}
                              </Box>
                            </Grid>

                            {/* Vehicle Details Column */}
                            <Grid item xs={12} sm={6}>
                              <Box p={1.5} bgcolor="#f8fafc" borderRadius={2} border="1px solid #f1f5f9" height="100%">
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Avatar sx={{ bgcolor: '#ede9fe', color: '#7c3aed', width: 26, height: 26 }}>
                                      <DirectionsCar sx={{ fontSize: 15 }} />
                                    </Avatar>
                                    <Typography variant="caption" color="text.secondary" fontWeight={750}>
                                      LOGISTICS & VEHICLE
                                    </Typography>
                                  </Stack>
                                  {day.vehicle_status && (
                                    <Chip
                                      label={day.vehicle_status}
                                      size="small"
                                      color={day.vehicle_status === 'Confirmed' ? 'success' : 'warning'}
                                      sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                                    />
                                  )}
                                </Stack>
                                <Typography variant="body2" fontWeight={700} color="text.primary" mt={0.5}>
                                  {day.vehicle_name || 'No Vehicle Assigned'}
                                  {day.vehicle_payment ? ` (₹${Number(day.vehicle_payment).toLocaleString()})` : ''}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Driver: <strong>{day.driver || 'Not Assigned'}</strong>
                                  {day.driver_phone ? ` (${day.driver_phone})` : ''}
                                </Typography>
                              </Box>
                            </Grid>

                            {/* Rejection Alert banner if exists */}
                            {latestRejection && (
                              <Grid item xs={12}>
                                <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ py: 0.5, px: 1.5, fontSize: 12 }}>
                                  <strong>Rejection note:</strong> {latestRejection.description}
                                  {latestRejection.date_time && ` (${dayjs(latestRejection.date_time).format('DD MMM YYYY, h:mm A')})`}
                                </Alert>
                              </Grid>
                            )}

                          </Grid>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )}

              {/* ── 2. CHECKLIST TAB ──────────────────────────────────────── */}
              {tab === 2 && (
                <TripChecklist
                  checklist={localTrip.checklist}
                  tripId={localTrip._id}
                />
              )}

              {/* ── 3. ACTIVITY TIMELINE TAB ──────────────────────────────── */}
              {tab === 3 && <ActivityTimeline tripId={localTrip._id} />}

              {/* ── 4. ESCALATIONS TAB ────────────────────────────────────── */}
              {tab === 4 && (
                <Stack spacing={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle2" fontWeight={700}>Trip Escalations</Typography>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Warning />}
                      onClick={() => setEscalateDialog(true)}
                      size="small"
                      sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
                    >
                      Raise New Escalation
                    </Button>
                  </Box>

                  {(!localTrip.escalations || localTrip.escalations.length === 0) && (
                    <Alert severity="success" icon={<CheckCircle />}>
                      No active escalations recorded for this trip.
                    </Alert>
                  )}

                  {(localTrip.escalations || []).map(esc => (
                    <Box
                      key={esc._id}
                      border="1px solid"
                      borderColor={esc.status === 'Open' ? 'error.light' : 'success.light'}
                      borderRadius={2}
                      p={2}
                      bgcolor="#ffffff"
                    >
                      <Stack direction="row" spacing={1} mb={1}>
                        <Chip
                          label={esc.severity}
                          color={['Critical', 'High'].includes(esc.severity) ? 'error' : 'warning'}
                          size="small"
                        />
                        <Chip
                          label={esc.status}
                          color={esc.status === 'Resolved' ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                      <Typography variant="body2">{esc.description}</Typography>
                      {esc.resolution && (
                        <Typography variant="caption" color="success.main" display="block" mt={0.5}>
                          ✓ {esc.resolution}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.disabled">
                        {dayjs(esc.created_at).format('DD MMM YYYY, h:mm A')}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}

            </Box>
          </>
        )}
      </Box>

      {/* ── EDIT DAY ITINERARY DIALOG ─────────────────────────────────────── */}
      <Dialog open={editDayOpen} onClose={() => setEditDayOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {canEditItinerary
            ? `Edit Day ${editDayIndex !== null ? editDayIndex + 1 : ''} Itinerary`
            : `Propose Day ${editDayIndex !== null ? editDayIndex + 1 : ''} Changes (Change Request)`}
        </DialogTitle>
        <DialogContent dividers>
          {!canEditItinerary && canCreateChangeRequest && (
            <Alert severity="warning" icon={<AddCommentIcon />} sx={{ mb: 2, py: 0.5, fontSize: 12 }}>
              You are proposing changes for <strong>Day {editDayIndex !== null ? editDayIndex + 1 : ''}</strong>. Modifying these details will submit an <strong>Itinerary Change Request</strong> for the Operation Manager to review.
            </Alert>
          )}
          <Stack spacing={2.5} mt={0.5}>
            
            {/* Destination & Journey Date */}
            <Typography variant="caption" fontWeight={800} color="primary.main" letterSpacing="0.05em">
              1. LOCATION & DATE
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={7}>
                <TextField
                  label="Day Destination / Place"
                  size="small"
                  fullWidth
                  value={editDayData.destination || ''}
                  onChange={e => setEditDayData({ ...editDayData, destination: e.target.value })}
                  placeholder="e.g. Gangtok, Lachen, Pelling, Darjeeling"
                  helperText="City or place of visit for this day"
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="Journey Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editDayData.journey_date || ''}
                  onChange={e => setEditDayData({ ...editDayData, journey_date: e.target.value })}
                />
              </Grid>
            </Grid>

            <Divider />

            {/* Hotel Section */}
            <Typography variant="caption" fontWeight={800} color="primary.main" letterSpacing="0.05em">
              2. ACCOMMODATION DETAILS
            </Typography>
            <Autocomplete
              size="small"
              freeSolo
              options={hotels}
              getOptionLabel={(option) => typeof option === 'string' ? option : `${option.hotel_name} (${option.sub_destination || option.location || 'Location'})`}
              value={hotels.find(h => h._id === editDayData.hotel_id || h.hotel_name === editDayData.hotel_name) || editDayData.hotel_name || null}
              onInputChange={(e, val) => {
                if (!hotels.find(h => h.hotel_name === val)) {
                  setEditDayData(prev => ({ ...prev, hotel_id: '', hotel_name: val }));
                }
              }}
              onChange={(e, val) => {
                if (typeof val === 'string') {
                  setEditDayData(prev => ({ ...prev, hotel_id: '', hotel_name: val }));
                  return;
                }
                const updated = {
                  ...editDayData,
                  hotel_id: val?._id || '',
                  hotel_name: val?.hotel_name || '',
                };
                const price = getPrice(val, updated.room_type, updated.meal_plan, localTrip?.trip_details?.hotel_season);
                setEditDayData({ ...updated, hotel_amount: price || updated.hotel_amount });
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                const priced = hasHotelPrice(option._id);
                return (
                  <Box
                    key={key}
                    component="li"
                    {...optionProps}
                    sx={{
                      backgroundColor: priced ? '#f0fdf4 !important' : '#ffffff !important',
                      '&:hover': { backgroundColor: priced ? '#dcfce7 !important' : '#f8fafc !important' }
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>{option.hotel_name}</Typography>
                    {(option.sub_destination || option.location) && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({option.sub_destination || option.location})
                      </Typography>
                    )}
                  </Box>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Select Hotel Name" />}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  freeSolo
                  options={roomOptions}
                  value={editDayData.room_type || ''}
                  onChange={(e, val) => {
                    const price = getPrice(selectedHotelObj, val, editDayData.meal_plan, localTrip?.trip_details?.hotel_season);
                    setEditDayData(prev => ({ ...prev, room_type: val || '', hotel_amount: price || prev.hotel_amount }));
                  }}
                  onInputChange={(e, val) => setEditDayData(prev => ({ ...prev, room_type: val }))}
                  renderInput={(params) => <TextField {...params} label="Room Type / Category" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={MEAL_PLANS}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
                  value={MEAL_PLANS.find(p => p.value === editDayData.meal_plan) || editDayData.meal_plan || null}
                  onChange={(e, val) => {
                    const planVal = typeof val === 'string' ? val : (val?.value || '');
                    const price = getPrice(selectedHotelObj, editDayData.room_type, planVal, localTrip?.trip_details?.hotel_season);
                    setEditDayData(prev => ({ ...prev, meal_plan: planVal, hotel_amount: price || prev.hotel_amount }));
                  }}
                  renderInput={(params) => <TextField {...params} label="Meal Plan" />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Hotel Amount (₹)"
                  size="small"
                  type="number"
                  fullWidth
                  value={editDayData.hotel_amount || ''}
                  onChange={e => setEditDayData({ ...editDayData, hotel_amount: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Check-in Date"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editDayData.checkin_date || ''}
                  onChange={e => setEditDayData({ ...editDayData, checkin_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Hotel Confirmation</InputLabel>
                  <Select
                    value={editDayData.hotel_confirmation || ''}
                    label="Hotel Confirmation"
                    onChange={e => setEditDayData({ ...editDayData, hotel_confirmation: e.target.value })}
                  >
                    <MenuItem value="">Pending</MenuItem>
                    <MenuItem value="Confirmed">Confirmed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Divider />

            {/* Vehicle & Logistics Section */}
            <Typography variant="caption" fontWeight={800} color="primary.main" letterSpacing="0.05em">
              3. VEHICLE & DRIVER LOGISTICS
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  freeSolo
                  options={uniqueVehicleNames}
                  value={editDayData.vehicle_name || ''}
                  onInputChange={(e, val) => setEditDayData(prev => ({ ...prev, vehicle_name: val }))}
                  onChange={(e, val) => setEditDayData(prev => ({ ...prev, vehicle_name: val || '' }))}
                  renderInput={(params) => <TextField {...params} label="Vehicle Model (e.g. Sedan, Innova)" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  freeSolo
                  options={driverOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name ? `${option.name} ${option.phone ? `(${option.phone})` : ''}` : (option.driver_name || '');
                  }}
                  value={editDayData.driver || ''}
                  onChange={(e, val) => {
                    if (typeof val === 'string') {
                      setEditDayData(prev => ({ ...prev, driver: val }));
                    } else if (val) {
                      setEditDayData(prev => ({
                        ...prev,
                        driver: val.name || val.driver_name || '',
                        driver_phone: val.phone || val.driver_phone || prev.driver_phone || ''
                      }));
                    } else {
                      setEditDayData(prev => ({ ...prev, driver: '' }));
                    }
                  }}
                  onInputChange={(e, val) => setEditDayData(prev => ({ ...prev, driver: val }))}
                  renderInput={(params) => <TextField {...params} label="Driver Name" />}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Driver Phone"
                  size="small"
                  fullWidth
                  value={editDayData.driver_phone || ''}
                  onChange={e => setEditDayData({ ...editDayData, driver_phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Vehicle Payment (₹)"
                  size="small"
                  type="number"
                  fullWidth
                  value={editDayData.vehicle_payment || ''}
                  onChange={e => setEditDayData({ ...editDayData, vehicle_payment: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Vehicle Status</InputLabel>
                  <Select
                    value={editDayData.vehicle_status || ''}
                    label="Vehicle Status"
                    onChange={e => setEditDayData({ ...editDayData, vehicle_status: e.target.value })}
                  >
                    <MenuItem value="">Pending</MenuItem>
                    <MenuItem value="Confirmed">Confirmed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDayOpen(false)} disabled={editDaySubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={canEditItinerary ? "primary" : "warning"}
            onClick={handleEditDaySave}
            disabled={editDaySubmitting}
            sx={{ fontWeight: 700, px: 3 }}
          >
            {editDaySubmitting
              ? 'Submitting...'
              : canEditItinerary
                ? 'Save Changes'
                : 'Submit Change Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── VERIFY DAY DIALOG ─────────────────────────────────────────────── */}
      <Dialog open={verifyOpen} onClose={() => setVerifyOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Verify Itinerary (Day {verifyIndex !== null ? verifyIndex + 1 : ''})
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Review and approve or reject Day {verifyIndex !== null ? verifyIndex + 1 : ''}'s accommodation and vehicle bookings.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant={verifyStatus === 'Approved' ? 'contained' : 'outlined'}
                color="success"
                onClick={() => setVerifyStatus('Approved')}
                fullWidth
                sx={{ fontWeight: 700, py: 1 }}
              >
                Approve Day
              </Button>
              <Button
                variant={verifyStatus === 'Rejected' ? 'contained' : 'outlined'}
                color="error"
                onClick={() => setVerifyStatus('Rejected')}
                fullWidth
                sx={{ fontWeight: 700, py: 1 }}
              >
                Reject Day
              </Button>
            </Stack>
            {verifyStatus === 'Rejected' && (
              <TextField
                label="Rejection Reason"
                fullWidth
                multiline
                rows={3}
                placeholder="State the reason for rejection (e.g. hotel over budget, vehicle mismatch)..."
                value={verifyReason}
                onChange={(e) => setVerifyReason(e.target.value)}
                size="small"
                autoFocus
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setVerifyOpen(false)} disabled={verifySubmitting}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!verifyStatus || (verifyStatus === 'Rejected' && !verifyReason.trim()) || verifySubmitting}
            onClick={handleVerifySubmit}
            sx={{ fontWeight: 700, px: 3 }}
          >
            {verifySubmitting ? 'Saving...' : 'Confirm Verification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── VIEW REJECTIONS DIALOG ────────────────────────────────────────── */}
      <Dialog open={rejectedViewOpen} onClose={() => setRejectedViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon /> Rejected Days History
        </DialogTitle>
        <DialogContent dividers>
          {rejectedDays.length === 0 ? (
            <Alert severity="success">No rejected days recorded for this trip.</Alert>
          ) : (
            <Stack spacing={2}>
              {rejectedDays.map((rd, i) => (
                <Box key={i} p={2} border="1px solid #fee2e2" borderRadius={2} bgcolor="#fff5f5">
                  <Typography variant="subtitle2" fontWeight={800} color="error.dark">
                    Day {rd.dayNum} - {rd.destination}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Stack spacing={1}>
                    {rd.reasons.map((r, rIdx) => (
                      <Box key={rIdx}>
                        <Typography variant="body2" color="text.primary">
                          • {r.description || r}
                        </Typography>
                        {r.date_time && (
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(r.date_time).format('DD MMM YYYY, h:mm A')}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectedViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── CHANGE REQUEST DIALOG ─────────────────────────────────────────── */}
      <Dialog open={changeRequestOpen} onClose={() => setChangeRequestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Submit Change Request</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Describe the itinerary or booking modifications needed. Operation managers will be notified to review and update.
          </Typography>
          <TextField
            label="Change Request Description"
            fullWidth
            multiline
            rows={4}
            value={changeRequestDesc}
            onChange={(e) => setChangeRequestDesc(e.target.value)}
            placeholder="Specify what hotels, dates, or vehicles should be changed..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setChangeRequestOpen(false)} disabled={crSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleCRSubmit} disabled={!changeRequestDesc.trim() || crSubmitting} sx={{ fontWeight: 700 }}>
            {crSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT GUEST & STAY DIALOG ──────────────────────────────────────── */}
      <Dialog open={guestEditOpen} onClose={() => setGuestEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Edit Guest & Stay Information</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Guest Name"
                  value={guestEditData.name || ''}
                  onChange={e => setGuestEditData({ ...guestEditData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Country Code"
                  value={guestEditData.country_code || '+91'}
                  onChange={e => setGuestEditData({ ...guestEditData, country_code: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contact Phone"
                  value={guestEditData.contact || ''}
                  onChange={e => setGuestEditData({ ...guestEditData, contact: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email Address"
                  value={guestEditData.email || ''}
                  onChange={e => setGuestEditData({ ...guestEditData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Total Adults"
                  value={guestEditData.pax || ''}
                  onChange={e => setGuestEditData({ ...guestEditData, pax: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Kids Above 5"
                  value={guestEditData.kids_above_5 || 0}
                  onChange={e => setGuestEditData({ ...guestEditData, kids_above_5: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Preferred Hotel Category"
                  value={guestEditData.hotel || ''}
                  onChange={e => setGuestEditData({ ...guestEditData, hotel: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Room Type"
                  value={guestEditData.roomType || ''}
                  onChange={e => setGuestEditData({ ...guestEditData, roomType: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="No. of Rooms"
                  value={guestEditData.rooms || 1}
                  onChange={e => setGuestEditData({ ...guestEditData, rooms: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Vehicle Model"
                  value={guestEditData.carname || ''}
                  onChange={e => setGuestEditData({ ...guestEditData, carname: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Vehicle Count"
                  value={guestEditData.carcount || 1}
                  onChange={e => setGuestEditData({ ...guestEditData, carcount: e.target.value })}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setGuestEditOpen(false)} disabled={guestEditSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveGuestEdit} disabled={guestEditSubmitting} sx={{ fontWeight: 700 }}>
            {guestEditSubmitting ? 'Saving...' : 'Save Details'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── EDIT BOOKING & FINANCIALS DIALOG ──────────────────────────────── */}
      <Dialog open={bookingEditOpen} onClose={() => setBookingEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Edit Booking & Financial Details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Lead Source"
                  value={bookingEditData.source || ''}
                  onChange={e => setBookingEditData({ ...bookingEditData, source: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Tour / Travel Date"
                  InputLabelProps={{ shrink: true }}
                  value={bookingEditData.tourDate || ''}
                  onChange={e => setBookingEditData({ ...bookingEditData, tourDate: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Booking Stage</InputLabel>
                  <Select
                    value={bookingEditData.bookingStatus || ''}
                    label="Booking Stage"
                    onChange={e => setBookingEditData({ ...bookingEditData, bookingStatus: e.target.value })}
                  >
                    {['Upcoming', 'Running', 'Completed', 'Cancelled', 'On Hold', 'Booked', 'Confirmed'].map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Total Package Cost (₹)"
                  value={bookingEditData.cost || ''}
                  onChange={e => setBookingEditData({ ...bookingEditData, cost: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Advance Payment (₹)"
                  value={bookingEditData.advancePayment || ''}
                  onChange={e => setBookingEditData({ ...bookingEditData, advancePayment: e.target.value })}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setBookingEditOpen(false)} disabled={bookingEditSubmitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBookingEdit} disabled={bookingEditSubmitting} sx={{ fontWeight: 700 }}>
            {bookingEditSubmitting ? 'Saving...' : 'Save Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auxiliary Dialogs */}
      <RaiseEscalationDialog
        open={escalateDialog}
        tripId={localTrip?._id}
        onClose={() => setEscalateDialog(false)}
        onSuccess={refreshList}
      />
      <CollectionFollowupDialog
        open={collectDialog}
        trip={localTrip}
        onClose={() => {
          setCollectDialog(false);
          if (localTrip) refreshList();
        }}
      />
      <SnackbarComponent />
    </Drawer>
  );
};

export default TripDetailDrawer;
